import React, { useState, useEffect, useRef, useMemo } from 'react';
import { HiPencil, HiEye, HiTrash, HiPlus, HiSearch, HiX, HiCollection, HiCurrencyDollar, HiSwitchHorizontal, HiDocumentReport } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { useNotification } from '../context/NotificationContext';
import ConfirmationModal from '../components/ConfirmationModal';
import Tooltip from '../components/Tooltip';
import ColorSelect from '../components/ColorSelect';
import CitySelect from '../components/CitySelect';
import { getErrorMessage } from '../utils/errorHandler';
import { formatDate } from '../utils/dateFormatter';
import { formatPKR } from '../utils/currencyFormatter';
import { formatPartySearchBalanceLabel, partySearchBalanceClassName } from '../utils/partySearchBalance';
import { API } from '../config';
import axios from 'axios';
import { downloadStockReportPdf } from '../utils/stockReportPdf';
import { downloadProductListPdf } from '../utils/productListPdf';
import './Products.css';

const canEditOrDeleteProduct = (role) => role === 'super_admin' || role === 'admin';

const Products = () => {
  const { user } = useAuth();
  const { hasBranches, effectiveBranchId, isAdmin, currentBranch, branches } = useBranch();
  const isMainBranch = currentBranch && (currentBranch.is_main === true || currentBranch.isMain === true);
  const canEditProduct = canEditOrDeleteProduct(user?.role);
  const navigate = useNavigate();
  const { success, error } = useNotification();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [submittingProduct, setSubmittingProduct] = useState(false);
  const [stockReportPdfLoading, setStockReportPdfLoading] = useState(false);
  const [productListPdfLoading, setProductListPdfLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showOurCreations, setShowOurCreations] = useState(false);
  const [ourCreationsWorkOrders, setOurCreationsWorkOrders] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(user.business_id || '');
  const [vendors, setVendors] = useState([]);
  const [inHouseVendorId, setInHouseVendorId] = useState(null);
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const vendorDropdownRef = useRef(null);
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [vendorFormData, setVendorFormData] = useState({
    name: '',
    phone: '',
    city: '',
    address: '',
    openingBalanceDirection: 'you_owe_them',
    openingBalanceAmount: ''
  });

  // Close vendor dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(e.target)) {
        setShowVendorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [formData, setFormData] = useState({
    productCode: '',
    name: '',
    description: '',
    productType: 'stitched',
    unitType: 'piece', // Auto-set: 'piece' for stitched, 'guz' for unstitched
    costPrice: '',
    costPerMeter: '',
    salePrice: '',
    quantity: '',
    totalMeters: '',
    vendorId: '',
    purchaseDate: ''
  });
  const [stitchedSizeBreakdown, setStitchedSizeBreakdown] = useState([{ size: '', quantity: '', costPrice: '', barcode: '' }]); // mandatory for stitched
  const [variantFormData, setVariantFormData] = useState({
    color: '',
    size: '',
    costPrice: '',
    salePrice: '',
    sku: '',
    barcode: ''
  });
  const [unstitchedVariants, setUnstitchedVariants] = useState([{ color: '', meters: '', costPrice: '', salePrice: '' }]);
  const [branchesList, setBranchesList] = useState([]);
  const [showAddStockModal, setShowAddStockModal] = useState(false);
  const [addStockRowInputs, setAddStockRowInputs] = useState({}); // { [variantId]: { quantity: '', cost: '' } }
  const [addStockNewRows, setAddStockNewRows] = useState([]); // [{ id, color, size, quantity, cost }] for new variants
  const [addStockPurchaseDate, setAddStockPurchaseDate] = useState('');
  const [addStockSubmitting, setAddStockSubmitting] = useState(false);
  const [addStockCreatedPurchase, setAddStockCreatedPurchase] = useState(null);
  const [addStockBillPartyId, setAddStockBillPartyId] = useState('');
  const [addStockUpdatePartyLedger, setAddStockUpdatePartyLedger] = useState(true);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferVariant, setTransferVariant] = useState(null);
  const getTodayStr = () => new Date().toISOString().split('T')[0];
  const [transferForm, setTransferForm] = useState({ fromBranchId: '', toBranchId: '', quantity: '', unitPrice: '', transferDate: '' });
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyFilters, setHistoryFilters] = useState({ branchId: '', fromDate: '', toDate: '', type: '' });
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedGroupKey, setExpandedGroupKey] = useState(null);
  const historyModalOpenedRef = useRef(false);
  const [editingVariantForPrice, setEditingVariantForPrice] = useState(null);
  const [updatePriceFormData, setUpdatePriceFormData] = useState({ costPrice: '', salePrice: '' });
  const [selectedForSale, setSelectedForSale] = useState(new Set());
  /** businesses.meta.postProductPurchaseToPartyLedger — when false, server skips vendor ledger on product/stock purchase flows */
  const [businessProductLedgerEnabled, setBusinessProductLedgerEnabled] = useState(true);
  const [postPurchaseToPartyLedger, setPostPurchaseToPartyLedger] = useState(true);

  useEffect(() => {
    if (user.role === 'super_admin') {
      fetchBusinesses();
    } else {
      fetchVendors();
    }
  }, [user.role, effectiveBranchId]);

  useEffect(() => {
    if (selectedBusinessId) {
      fetchVendors();
    }
  }, [selectedBusinessId, effectiveBranchId]);

  const fetchBranches = async () => {
    try {
      let list = [];
      if (user.role === 'super_admin') {
        const businessId = selectedBusinessId;
        if (!businessId) return;
        const response = await axios.get(`${API}/businesses/${businessId}/branches`);
        list = response.data.branches || [];
      } else {
        // Admin: all branches; Manager: only their branch
        const response = await axios.get(`${API}/branches`);
        list = response.data.branches || [];
      }
      setBranchesList(list);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  useEffect(() => {
    if ((showModal && formData.productType === 'unstitched') || showDetailsModal) {
      fetchBranches();
    }
  }, [showModal, showDetailsModal, formData.productType, selectedBusinessId]);

  useEffect(() => {
    if (showModal && vendors.length > 0 && formData.productType === 'stitched' && !formData.vendorId && inHouseVendorId) {
      setFormData(prev => ({ ...prev, vendorId: inHouseVendorId.toString() }));
    }
    // Auto-set unit type based on product type
    if (showModal) {
      const expectedUnitType = formData.productType === 'stitched' ? 'piece' : 'guz';
      if (formData.unitType !== expectedUnitType) {
        setFormData(prev => ({ ...prev, unitType: expectedUnitType }));
      }
    }
  }, [showModal, vendors, inHouseVendorId, formData.productType]);

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/businesses/all`);
      const businessList = response.data.businesses || [];
      setBusinesses(businessList);
      if (!selectedBusinessId && businessList.length > 0) {
        setSelectedBusinessId(businessList[0].id);
      }
    } catch (err) {
      error('Failed to fetch businesses');
    }
  };

  const fetchVendors = async () => {
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId);
      if (!businessId) return;
      const params = new URLSearchParams({ role: 'vendor' });
      if (effectiveBranchId) params.set('branchId', effectiveBranchId);
      const response = await axios.get(`${API}/parties/business/${businessId}?${params.toString()}`);
      const vendorList = response.data.parties || [];
      
      // Find or note In-House vendor
      const inHouse = vendorList.find(v => v.name === 'In-House');
      if (inHouse) {
        setInHouseVendorId(inHouse.id);
      }
      
      setVendors(vendorList);
    } catch (err) {
      console.error('Error fetching vendors:', err);
    }
  };

  const handleCreateVendor = async (e) => {
    e.preventDefault();
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId);
      const amount = parseFloat(vendorFormData.openingBalanceAmount) || 0;
      const dir = vendorFormData.openingBalanceDirection;
      const openingBalance = (dir === 'they_owe_you' ? amount : dir === 'you_owe_them' ? -amount : 0);
      const payload = {
        name: vendorFormData.name,
        phone: vendorFormData.phone,
        city: vendorFormData.city,
        address: vendorFormData.address,
        openingBalance,
        isVendor: true,
        isCustomer: false,
        isTailor: false,
        isAdditional: false,
        businessId: parseInt(businessId)
      };
      if (effectiveBranchId) {
        payload.branchId = effectiveBranchId;
      }

      const response = await axios.post(`${API}/parties`, payload);
      success('Vendor created successfully!');
      setShowAddVendorModal(false);
      setVendorFormData({ name: '', phone: '', city: '', address: '', openingBalanceDirection: 'you_owe_them', openingBalanceAmount: '' });
      await fetchVendors();
      if (response.data.party) {
        setFormData(prev => ({ ...prev, vendorId: response.data.party.id.toString() }));
        setVendorSearchTerm('');
        setShowVendorDropdown(false);
      }
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  useEffect(() => {
    if (selectedBusinessId) {
      fetchProducts();
    }
  }, [typeFilter, selectedBusinessId, showOurCreations, effectiveBranchId]);

  useEffect(() => {
    const bid = user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId);
    if (!bid) return;
    let cancelled = false;
    axios
      .get(`${API}/businesses/${bid}`)
      .then((res) => {
        if (cancelled) return;
        const m = res.data?.business?.meta;
        setBusinessProductLedgerEnabled(m?.postProductPurchaseToPartyLedger !== false);
      })
      .catch(() => {
        if (!cancelled) setBusinessProductLedgerEnabled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedBusinessId, user.business_id, user.businessId, user.role]);

  useEffect(() => {
    if (showModal && !editingProduct) {
      setPostPurchaseToPartyLedger(businessProductLedgerEnabled);
    }
  }, [showModal, editingProduct, businessProductLedgerEnabled]);

  const selectedVendor = vendors.find(v => v.id.toString() === formData.vendorId?.toString());
  const vendorDisplayText = selectedVendor
    ? `${selectedVendor.name}${selectedVendor.city ? ` (${selectedVendor.city})` : ''}`
    : '';

  // Filter vendors based on search term (searches name, city, phone)
  const filteredVendorsList = vendors.filter(v => {
    const q = (vendorSearchTerm || '').toLowerCase().trim();
    if (!q) return true;
    const name = (v.name || '').toLowerCase();
    const city = (v.city || '').toLowerCase();
    const phone = (v.phone || '').replace(/\D/g, '');
    const phoneQuery = q.replace(/\D/g, '');
    return name.includes(q) || city.includes(q) || (phoneQuery && phone.includes(phoneQuery));
  });

  const fetchProducts = async () => {
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
      if (!businessId) {
        setLoading(false);
        return;
      }
      if (showOurCreations) {
        const response = await axios.get(`${API}/work-orders/business/${businessId}?ourCreations=true`);
        setOurCreationsWorkOrders(response.data.work_orders || []);
      } else {
        let url = `${API}/products/business/${businessId}`;
        const params = new URLSearchParams();
        if (typeFilter) params.append('productType', typeFilter);
        if (searchTerm) params.append('search', searchTerm);
        if (effectiveBranchId) params.set('branchId', effectiveBranchId);
        if (params.toString()) url += `?${params.toString()}`;
        const response = await axios.get(url);
        setProducts(response.data.products || []);
      }
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchProducts();
  };

  const handleDownloadStockReportPdf = async () => {
    if (!hasBranches) {
      error('Create a branch first.');
      return;
    }
    if (isAdmin && !effectiveBranchId) {
      error('Select a branch in the header. The PDF lists stock for that branch.');
      return;
    }
    if (!effectiveBranchId) {
      error('Select a branch to export stock.');
      return;
    }
    const businessId = user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId);
    if (!businessId) {
      error('Select a business.');
      return;
    }
    if (stockReportPdfLoading) return;
    setStockReportPdfLoading(true);
    try {
      const [invRes, bizRes] = await Promise.all([
        axios.get(`${API}/inventory/branch/${effectiveBranchId}`),
        axios.get(`${API}/businesses/${businessId}`)
      ]);
      const inventory = invRes.data?.inventory || [];
      const biz = bizRes.data?.business || bizRes.data;
      const businessName = biz?.name || 'Business';
      const branchName = currentBranch?.name || user?.branch?.name || `Branch ${effectiveBranchId}`;
      await downloadStockReportPdf({ businessName, branchName, inventory });
      success('Stock report PDF downloaded.');
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setStockReportPdfLoading(false);
    }
  };

  const handleDownloadProductListPdf = async () => {
    if (showOurCreations) {
      error('Switch back to the product list to export search results.');
      return;
    }
    if (!hasBranches) {
      error('Create a branch first.');
      return;
    }
    if (isAdmin && !effectiveBranchId) {
      error('Select a branch in the header first.');
      return;
    }
    if (!products.length) {
      error('No products to export. Run a search or clear filters to load products.');
      return;
    }
    const businessId = user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId);
    if (!businessId) {
      error('Select a business.');
      return;
    }
    if (productListPdfLoading) return;
    setProductListPdfLoading(true);
    try {
      const bizRes = await axios.get(`${API}/businesses/${businessId}`);
      const biz = bizRes.data?.business || bizRes.data;
      const businessName = biz?.name || 'Business';
      const branchName = currentBranch?.name || user?.branch?.name || `Branch ${effectiveBranchId}`;
      await downloadProductListPdf({
        businessName,
        branchName,
        products,
        searchTerm,
        typeFilter,
      });
      success(searchTerm.trim() ? 'Search results PDF downloaded.' : 'Product list PDF downloaded.');
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setProductListPdfLoading(false);
    }
  };

  // Filter Our Creations work orders by search
  const filteredOurCreations = (ourCreationsWorkOrders || []).filter(wo => {
    if (!searchTerm || !searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase().trim();
    const productName = (wo.output_product?.name || '').toLowerCase();
    const productCode = (wo.output_product?.product_code || '').toLowerCase();
    const tailorName = (wo.tailor?.name || '').toLowerCase();
    const vendorName = (wo.vendor?.name || wo.input_product?.vendor?.name || '').toLowerCase();
    const woNum = (wo.work_order_number || '').toLowerCase();
    return productName.includes(q) || productCode.includes(q) || tailorName.includes(q) || vendorName.includes(q) || woNum.includes(q);
  });

  const handleViewDetails = async (productId) => {
    try {
      const params = effectiveBranchId ? { branchId: effectiveBranchId } : {};
      const response = await axios.get(`${API}/products/${productId}`, { params });
      setSelectedProduct(response.data.product);
      setShowDetailsModal(true);
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    const isUnstitched = product.product_type === 'unstitched';
    const vendorId = product.vendor?.id || product.vendor_id || '';
    setFormData({
      productCode: product.product_code,
      name: product.name,
      description: product.description || '',
      productType: product.product_type,
      unitType: product.unit_type,
      costPrice: isUnstitched ? '' : (product.cost_price || ''),
      costPerMeter: isUnstitched ? (product.cost_price || '') : '',
      salePrice: product.sale_price || '',
      quantity: isUnstitched ? '' : (product.quantity || ''),
      totalMeters: isUnstitched ? (product.quantity || '') : '',
      vendorId: vendorId.toString()
    });
    setVendorSearchTerm('');
    setShowVendorDropdown(false);
    setShowModal(true);
  };

  const handleDelete = (productId) => {
    setProductToDelete(productId);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/products/${productToDelete}`);
      success('Product deleted successfully!');
      setShowConfirmModal(false);
      setProductToDelete(null);
      fetchProducts();
    } catch (err) {
      error(getErrorMessage(err));
      setShowConfirmModal(false);
      setProductToDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasBranches) {
      error('Create at least one branch before adding products. Go to Branches and add a branch.');
      return;
    }
    if (isAdmin && !effectiveBranchId) {
      error('Select a branch from the header dropdown to add a product.');
      return;
    }
    setSubmittingProduct(true);
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId);
      
      // Validate unstitched variants
      if (formData.productType === 'unstitched' && !editingProduct) {
        const validVariants = unstitchedVariants.filter(v => v.color.trim() && (parseFloat(v.meters) || 0) > 0);
        if (validVariants.length === 0) {
          error('Add at least one variant with color and guz');
          return;
        }
        const colors = validVariants.map(v => v.color.trim().toLowerCase());
        const hasDuplicateColor = colors.some((c, i) => colors.indexOf(c) !== i);
        if (hasDuplicateColor) {
          error('Duplicate colors are not allowed. Each variant must have a unique color.');
          return;
        }
      }

      // Validate stitched size breakdown (mandatory): at least one row, sum must equal total units
      if (formData.productType === 'stitched' && !editingProduct) {
        const validRows = stitchedSizeBreakdown.filter(r => (parseInt(r.quantity, 10) || 0) > 0);
        const totalFromSizes = validRows.reduce((sum, r) => sum + (parseInt(r.quantity, 10) || 0), 0);
        const totalUnits = parseInt(formData.quantity, 10) || 0;
        if (validRows.length === 0) {
          error('Size breakdown is required. Add at least one size with quantity.');
          return;
        }
        if (totalFromSizes !== totalUnits) {
          error(`Size breakdown total (${totalFromSizes}) must equal Number of Units (${totalUnits}).`);
          return;
        }
        const sizesUsed = validRows.map(r => (r.size != null && r.size !== '') ? r.size : '__one__');
        const duplicateSize = sizesUsed.some((s, i) => sizesUsed.indexOf(s) !== i);
        if (duplicateSize) {
          error('Each size can only appear once in the breakdown.');
          return;
        }
      }
      
      // Product name and code are required
      const trimmedName = (formData.name || '').trim();
      const trimmedCode = (formData.productCode || '').trim();
      if (!trimmedName) {
        error('Product name is required.');
        return;
      }
      if (trimmedName.length < 2) {
        error('Product name must be at least 2 characters.');
        return;
      }
      if (!trimmedCode) {
        error('Product code is required.');
        return;
      }
      if (/\s/.test(trimmedCode)) {
        error('Product code cannot contain spaces.');
        return;
      }
      
      // Prepare payload based on product type
      const payload = {
        productCode: trimmedCode,
        name: trimmedName,
        description: formData.description || '',
        productType: formData.productType,
        unitType: formData.productType === 'stitched' ? 'piece' : 'guz',
        salePrice: formData.salePrice ? parseFloat(formData.salePrice) : null,
        vendorId: formData.vendorId ? parseInt(formData.vendorId) : null,
        businessId: parseInt(businessId)
      };
      if (effectiveBranchId) payload.branchId = effectiveBranchId;
      if (!editingProduct && formData.purchaseDate) payload.purchaseDate = formData.purchaseDate;
      if (!editingProduct) {
        payload.postPurchaseToPartyLedger =
          !!postPurchaseToPartyLedger && businessProductLedgerEnabled;
      }

      // Set cost price and quantity based on product type
      if (formData.productType === 'stitched') {
        payload.costPrice = stitchedAverageCostPrice() || null;
        payload.quantity = formData.quantity ? parseInt(formData.quantity) : 0;
        const validBreakdown = (stitchedSizeBreakdown || []).filter(r => (parseInt(r.quantity, 10) || 0) > 0);
        payload.sizeBreakdown = validBreakdown.map(r => ({
          size: (r.size && String(r.size).trim()) || null,
          quantity: parseInt(r.quantity, 10) || 0,
          costPrice: (r.costPrice !== '' && r.costPrice != null) ? parseFloat(r.costPrice) : null,
          barcode: (r.barcode && String(r.barcode).trim()) ? String(r.barcode).trim() : null
        }));
      } else {
        payload.costPrice = null;
        if (!editingProduct && unstitchedVariants.some(v => v.color.trim() && (parseFloat(v.meters) || 0) > 0)) {
          payload.variants = unstitchedVariants
            .filter(v => v.color.trim() && (parseFloat(v.meters) || 0) >= 0)
            .map(v => ({
              color: v.color.trim(),
              meters: parseFloat(v.meters) || 0,
              costPrice: (v.costPrice !== '' && v.costPrice != null) ? parseFloat(v.costPrice) : null,
              salePrice: (v.salePrice !== '' && v.salePrice != null) ? parseFloat(v.salePrice) : null
            }));
          payload.quantity = 0;
        } else {
          payload.quantity = editingProduct
            ? (parseFloat(editingProduct.quantity) || 0)
            : unstitchedVariants.reduce((s, v) => s + (parseFloat(v.meters) || 0), 0);
        }
      }

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload);
        success('Product updated successfully!');
      } else {
        await axios.post(`${API}/products`, payload);
        success('Product created successfully!');
      }
      resetForm();
      fetchProducts();
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setSubmittingProduct(false);
    }
  };

  const handleAddVariant = async (e) => {
    e.preventDefault();
    if (!variantFormData.color?.trim()) {
      error('Please select a color');
      return;
    }
    const branchId = effectiveBranchId ?? user?.branch_id ?? user?.branchId;
    if (isAdmin && !branchId) {
      error('Select a branch in the top bar to add a variant.');
      return;
    }
    const colorLower = variantFormData.color.trim().toLowerCase();
    const existingColor = selectedProduct?.variants?.some(
      v => (v.color || '').toLowerCase() === colorLower
    );
    if (existingColor) {
      error('A variant with this color already exists for this product');
      return;
    }
    try {
      const payload = {
        ...variantFormData,
        costPrice: variantFormData.costPrice ? parseFloat(variantFormData.costPrice) : null,
        salePrice: variantFormData.salePrice ? parseFloat(variantFormData.salePrice) : null
      };
      if (branchId) payload.branchId = branchId;
      // Barcode only for stitched products
      if (selectedProduct?.product_type !== 'stitched') {
        delete payload.barcode;
      } else if (payload.barcode !== undefined && (!payload.barcode || !String(payload.barcode).trim())) {
        payload.barcode = null;
      }
      await axios.post(`${API}/products/${selectedProduct.id}/variants`, payload);
      success('Variant added successfully!');
      setShowVariantModal(false);
      setVariantFormData({ color: '', size: '', costPrice: '', salePrice: '', sku: '', barcode: '' });
      // Refresh product details
      handleViewDetails(selectedProduct.id);
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleAddStock = async (e) => {
    e.preventDefault();
    if (isAdmin && !effectiveBranchId) {
      error('Select a branch in the top bar to add stock.');
      return;
    }
    const branchId = effectiveBranchId || user.branch_id || user.branchId;
    const productId = selectedProduct?.id;
    const variants = selectedProduct?.variants || [];
    const isStitchedProduct = selectedProduct?.product_type === 'stitched' || selectedProduct?.productType === 'stitched';
    const existingUnstitchedColors = (variants || [])
      .map(v => (v.color || '').trim())
      .filter(Boolean);
    const existingStitchedSizes = (variants || [])
      .map(v => (v.size || '').trim())
      .filter(Boolean);
    const selectedNewUnstitchedColors = (addStockNewRows || [])
      .map(r => (r.color || '').trim())
      .filter(Boolean);
    const selectedNewStitchedSizes = (addStockNewRows || [])
      .map(r => (r.size || '').trim())
      .filter(Boolean);

    // If user entered qty for a new row, require required fields (size for stitched, color for unstitched)
    const invalidNewRow = (addStockNewRows || []).find((r) => {
      const q = parseFloat(r.quantity);
      if (isNaN(q) || q <= 0) return false;
      if (isStitchedProduct) return !(r.size && String(r.size).trim());
      return !(r.color && String(r.color).trim());
    });
    if (invalidNewRow) {
      error(isStitchedProduct
        ? 'Please select the size for the new variant before adding stock (bill cannot be created without size).'
        : 'Please select the color for the new variant before adding stock (bill cannot be created without color).');
      return;
    }
    const toAdd = variants.filter((v) => {
      const row = addStockRowInputs[v.id] || { quantity: '', cost: '' };
      const q = parseFloat(row.quantity);
      return !isNaN(q) && q > 0;
    });
    const newRowsToAdd = addStockNewRows.filter((r) => {
      const key = isStitchedProduct ? (r.size || '').trim() : (r.color || '').trim();
      const q = parseFloat(r.quantity);
      return key.length > 0 && !isNaN(q) && q > 0;
    });
    if (toAdd.length === 0 && newRowsToAdd.length === 0) {
      error(isStitchedProduct
        ? 'Enter quantity to add for at least one variant, or add a new size variant with quantity.'
        : 'Enter quantity to add for at least one variant, or add a new color variant with quantity.');
      return;
    }
    for (const v of toAdd) {
      const row = addStockRowInputs[v.id] || { quantity: '', cost: '' };
      const enteredCost = row.cost !== '' && row.cost != null ? parseFloat(row.cost) : null;
      const effectiveCost = enteredCost != null && !isNaN(enteredCost)
        ? enteredCost
        : (v.cost_price != null ? parseFloat(v.cost_price) : (selectedProduct?.cost_price != null ? parseFloat(selectedProduct.cost_price) : null));

      if (effectiveCost !== null && (isNaN(effectiveCost) || effectiveCost < 0)) {
        error(`Invalid cost for variant ${v.color}${v.size ? ` ${v.size}` : ''}.`);
      return;
    }
    }

    for (const r of newRowsToAdd) {
      const enteredCost = r.cost !== '' && r.cost != null ? parseFloat(r.cost) : null;
      const effectiveCost = enteredCost != null && !isNaN(enteredCost)
        ? enteredCost
        : (selectedProduct?.cost_price != null ? parseFloat(selectedProduct.cost_price) : null);

      if (effectiveCost !== null && (isNaN(effectiveCost) || effectiveCost < 0)) {
        error(`Invalid cost for new variant ${isStitchedProduct ? (r.size || '').trim() : (r.color || '').trim()}.`);
        return;
      }

      if (isStitchedProduct) {
        const sz = (r.size || '').trim();
        if (!sz) {
          error('Size is required for new stitched variant.');
          return;
        }
        const dup = existingStitchedSizes.some(s => s.toLowerCase() === sz.toLowerCase());
        if (dup) {
          error(`Size "${sz}" already exists in this product.`);
          return;
        }
        const dupInNew = selectedNewStitchedSizes.filter(s => s.toLowerCase() === sz.toLowerCase()).length > 1;
        if (dupInNew) {
          error(`Size "${sz}" is selected more than once in new rows.`);
          return;
        }
      } else {
        const c = (r.color || '').trim();
        if (!c) {
          error('Color is required for new unstitched variant.');
          return;
        }
        const dup = existingUnstitchedColors.some(x => x.toLowerCase() === c.toLowerCase());
        if (dup) {
          error(`Color "${c}" already exists in this product.`);
          return;
        }
        const dupInNew = selectedNewUnstitchedColors.filter(x => x.toLowerCase() === c.toLowerCase()).length > 1;
        if (dupInNew) {
          error(`Color "${c}" is selected more than once in new rows.`);
          return;
        }
      }
    }
    setAddStockSubmitting(true);
    try {
      const existingItems = toAdd.map((v) => {
        const row = addStockRowInputs[v.id] || { quantity: '', cost: '' };
        const qty = parseFloat(row.quantity);
        const enteredCost = row.cost !== '' && row.cost != null ? parseFloat(row.cost) : null;
        const effectiveCost = enteredCost != null && !isNaN(enteredCost)
          ? enteredCost
          : (v.cost_price != null ? parseFloat(v.cost_price) : (selectedProduct?.cost_price != null ? parseFloat(selectedProduct.cost_price) : 0));
        return {
          variantId: v.id,
          quantity: qty,
          costPerMeter: effectiveCost
        };
      });

      const newItems = newRowsToAdd.map((r) => {
        const enteredCost = r.cost !== '' && r.cost != null ? parseFloat(r.cost) : null;
        const effectiveCost = enteredCost != null && !isNaN(enteredCost)
          ? enteredCost
          : (selectedProduct?.cost_price != null ? parseFloat(selectedProduct.cost_price) : 0);
        return {
          quantity: parseFloat(r.quantity),
          costPerMeter: effectiveCost,
          color: isStitchedProduct ? undefined : (r.color || '').trim(),
          size: isStitchedProduct ? (r.size || '').trim() : undefined
        };
      });

      const payload = {
        branchId: parseInt(branchId, 10),
        productId: parseInt(productId, 10),
        purchaseDate: addStockPurchaseDate || undefined,
        partyId: addStockBillPartyId ? parseInt(addStockBillPartyId, 10) : undefined,
        updatePartyLedger: !!addStockUpdatePartyLedger,
        items: [...existingItems, ...newItems]
      };

      const res = await axios.post(`${API}/inventory/add-stock`, payload);
      const purchase = res.data?.purchase || null;
      const purchaseNo = purchase?.purchase_number || purchase?.purchaseNumber;
      const variantCount = payload.items.length;
      setAddStockCreatedPurchase(purchase);
      success(
        purchaseNo
          ? `Stock added for ${variantCount} variant(s). Bill created: ${purchaseNo}`
          : `Stock added for ${variantCount} variant(s).`
      );
      // Return user back to Product Details (eye) by closing Add Stock modal
      setShowAddStockModal(false);
      setAddStockRowInputs({});
      setAddStockNewRows([]);
      setAddStockPurchaseDate('');
      if (productId) {
        const params = effectiveBranchId ? { branchId: effectiveBranchId } : {};
        const res = await axios.get(`${API}/products/${productId}`, { params });
        const updated = res.data.product;
        setSelectedProduct(updated);
        setProducts(prev => prev.map(p => p.id === productId ? { ...p, ...updated } : p));
      }
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setAddStockSubmitting(false);
    }
  };

  const handleTransferStock = async (e) => {
    e.preventDefault();
    const mainBranchForTransfer = (branchesList || []).find((b) => b.is_main || b.isMain);
    const fromBranchId = mainBranchForTransfer ? String(mainBranchForTransfer.id) : (transferForm.fromBranchId || (branchesList?.[0] ? String(branchesList[0].id) : ''));
    if (!transferVariant || !fromBranchId || !transferForm.toBranchId || !transferForm.quantity) {
      error('Select to branch and enter quantity. From branch is always the main branch.');
      return;
    }
    const qty = parseFloat(transferForm.quantity);
    if (isNaN(qty) || qty <= 0) {
      error('Quantity must be a positive number.');
      return;
    }
    const maxQty = getVariantQuantityInBranch(transferVariant, fromBranchId);
    if (maxQty != null && qty > maxQty) {
      error(`Quantity cannot exceed available stock. Max limit is ${maxQty}.`);
      return;
    }
    setTransferSubmitting(true);
    try {
      const payload = {
        fromBranchId: parseInt(fromBranchId, 10),
        toBranchId: parseInt(transferForm.toBranchId, 10),
        variantId: transferVariant.id,
        quantity: qty
      };
      if (transferForm.unitPrice !== '' && transferForm.unitPrice != null) {
        const up = parseFloat(transferForm.unitPrice);
        if (!isNaN(up) && up >= 0) payload.unitPrice = up;
      }
      if (transferForm.transferDate) payload.transferDate = transferForm.transferDate;
      await axios.post(`${API}/inventory/transfer`, payload);
      success('Stock transferred successfully.');
      setShowTransferModal(false);
      setTransferVariant(null);
      setTransferForm({ fromBranchId: '', toBranchId: '', quantity: '', unitPrice: '', transferDate: '' });
      const productId = selectedProduct?.id;
      if (productId) {
        const params = effectiveBranchId ? { branchId: effectiveBranchId } : {};
        const res = await axios.get(`${API}/products/${productId}`, { params });
        setSelectedProduct(res.data.product);
        setProducts(prev => prev.map(p => p.id === productId ? { ...p, ...res.data.product } : p));
      }
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setTransferSubmitting(false);
    }
  };

  const handleOpenHistory = () => {
    const currentBranchId = effectiveBranchId ?? currentBranch?.id ?? (branchesList && branchesList[0]?.id) ?? '';
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - 6);
    const toDateStr = to.toISOString().split('T')[0];
    const fromDateStr = from.toISOString().split('T')[0];
    setHistoryFilters({
      branchId: currentBranchId ? String(currentBranchId) : '',
      fromDate: fromDateStr,
      toDate: toDateStr,
      type: ''
    });
    setHistoryData([]);
    setExpandedGroupKey(null);
    historyModalOpenedRef.current = true;
    setShowHistoryModal(true);
  };

  // Group history by date + type + reference + customer for aggregated view; each group has variants breakdown
  const historyGrouped = useMemo(() => {
    if (!historyData || historyData.length === 0) return [];
    const map = {};
    historyData.forEach((row) => {
      const key = `${row.date}_${row.movement_type}_${row.reference_number || ''}_${(row.customer_or_branch || '').toString()}`;
      if (!map[key]) {
        map[key] = {
          key,
          date: row.date,
          movementType: row.movement_type,
          typeLabel: row.type_label,
          referenceNumber: row.reference_number,
          customerOrBranch: row.customer_or_branch,
          totalQuantity: 0,
          variants: [],
          partyLedgerNotice: null
        };
      }
      map[key].totalQuantity += Number(row.quantity_change) || 0;
      if (row.party_ledger_notice && !map[key].partyLedgerNotice) {
        map[key].partyLedgerNotice = row.party_ledger_notice;
      }
      map[key].variants.push({
        id: row.id,
        variantLabel: row.variant_label || '—',
        quantityChange: row.quantity_change
      });
    });
    return Object.values(map);
  }, [historyData]);

  const handleApplyHistory = async () => {
    if (!selectedProduct?.id || !historyFilters.branchId) {
      error('Branch is required to view history.');
      return;
    }
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({ branchId: historyFilters.branchId });
      if (historyFilters.fromDate) params.set('fromDate', historyFilters.fromDate);
      if (historyFilters.toDate) params.set('toDate', historyFilters.toDate);
      if (historyFilters.type) params.set('type', historyFilters.type);
      const res = await axios.get(`${API}/products/${selectedProduct.id}/history?${params.toString()}`);
      setHistoryData(res.data.history || []);
    } catch (err) {
      error(getErrorMessage(err));
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Auto-fetch history when modal opens (branch and dates already set)
  useEffect(() => {
    if (!showHistoryModal || !selectedProduct?.id || !historyFilters.branchId || !historyModalOpenedRef.current) return;
    historyModalOpenedRef.current = false;
    const params = new URLSearchParams({ branchId: historyFilters.branchId });
    if (historyFilters.fromDate) params.set('fromDate', historyFilters.fromDate);
    if (historyFilters.toDate) params.set('toDate', historyFilters.toDate);
    if (historyFilters.type) params.set('type', historyFilters.type);
    setHistoryLoading(true);
    axios.get(`${API}/products/${selectedProduct.id}/history?${params.toString()}`)
      .then(res => setHistoryData(res.data.history || []))
      .catch(err => { error(getErrorMessage(err)); setHistoryData([]); })
      .finally(() => setHistoryLoading(false));
  }, [showHistoryModal]);

  const handleToggleVariantBreakdown = (groupKey) => {
    setExpandedGroupKey((prev) => (prev === groupKey ? null : groupKey));
  };

  const handleUpdatePrice = (variant) => {
    setEditingVariantForPrice(variant);
    setUpdatePriceFormData({
      costPrice: variant.cost_price ?? selectedProduct?.cost_price ?? '',
      salePrice: variant.sale_price ?? selectedProduct?.sale_price ?? ''
    });
  };

  const handleUpdatePriceSubmit = async (e) => {
    e.preventDefault();
    if (!editingVariantForPrice) return;
    const productId = selectedProduct?.id;
    try {
      await axios.put(`${API}/products/variants/${editingVariantForPrice.id}`, {
        costPrice: updatePriceFormData.costPrice ? parseFloat(updatePriceFormData.costPrice) : null,
        salePrice: updatePriceFormData.salePrice ? parseFloat(updatePriceFormData.salePrice) : null
      });
      success('Prices updated successfully');
      setEditingVariantForPrice(null);
      setUpdatePriceFormData({ costPrice: '', salePrice: '' });
      // Refetch product and update products list so list shows new total cost
      if (productId) {
        const params = effectiveBranchId ? { branchId: effectiveBranchId } : {};
        const res = await axios.get(`${API}/products/${productId}`, { params });
        const updated = res.data.product;
        setSelectedProduct(updated);
        setProducts(prev => prev.map(p => p.id === productId ? { ...p, ...updated } : p));
      }
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingProduct(null);
    setVendorSearchTerm('');
    setShowVendorDropdown(false);
    setUnstitchedVariants([{ color: '', meters: '', costPrice: '', salePrice: '' }]);
    setStitchedSizeBreakdown([{ size: '', quantity: '', costPrice: '', barcode: '' }]);
    setFormData({
      productCode: '',
      name: '',
      description: '',
      productType: 'stitched',
      unitType: 'piece',
      costPrice: '',
      costPerMeter: '',
      salePrice: '',
      quantity: '',
      totalMeters: '',
      vendorId: '',
      purchaseDate: ''
    });
  };

  // Check if color is light (for readable text on color chip)
  const isLightColor = (color) => {
    if (!color || typeof color !== 'string') return false;
    const c = color.toLowerCase().trim();
    const lightNames = ['white', 'yellow', 'lime', 'lightgray', 'lightgrey', 'ivory', 'beige', 'wheat', 'lemonchiffon', 'lightyellow', 'azure', 'lavender', 'lightcyan', 'linen', 'snow', 'mintcream', 'honeydew', 'floralwhite'];
    if (lightNames.includes(c)) return true;
    if (c.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)) {
      const hex = c.replace('#', '');
      const r = parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.slice(0, 2), 16);
      const g = parseInt(hex.length === 3 ? hex[1] + hex[1] : hex.slice(2, 4), 16);
      const b = parseInt(hex.length === 3 ? hex[2] + hex[2] : hex.slice(4, 6), 16);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.7;
    }
    return false;
  };

  // Quantity for a variant: branch-scoped (available_quantity) or sum of inventories
  const getVariantQuantity = (variant) => {
    if (variant.available_quantity != null && variant.available_quantity !== undefined) return parseFloat(variant.available_quantity) || 0;
    return (variant.inventories || []).reduce((s, inv) => s + parseFloat(inv.quantity || 0), 0);
  };

  // Quantity for a variant in a specific branch (for transfer modal). Returns null if unknown.
  const getVariantQuantityInBranch = (variant, branchId) => {
    if (!variant || branchId == null || branchId === '') return null;
    const bid = parseInt(branchId, 10);
    const invs = variant.inventories || [];
    if (invs.length > 0) {
      const inv = invs.find((i) => (i.branch_id ?? i.branchId) === bid);
      return inv != null ? parseFloat(inv.quantity || 0) : 0;
    }
    if (effectiveBranchId != null && bid === effectiveBranchId && (variant.available_quantity != null && variant.available_quantity !== undefined)) {
      return parseFloat(variant.available_quantity) || 0;
    }
    return null;
  };

  // Average cost price = sum of all variant cost prices / number of variants (simple average). Falls back to product.cost_price.
  const getLatestCostPrice = (product) => {
    if (!product) return null;
    if (product.variants?.length > 0) {
      const costs = product.variants.map(v => parseFloat(v.cost_price ?? v.costPrice ?? product.cost_price) || 0);
      const sum = costs.reduce((a, b) => a + b, 0);
      return costs.length ? Number((sum / costs.length).toFixed(2)) : (product.cost_price ?? null);
    }
    return product.cost_price;
  };

  // Calculate total cost based on product type
  // Compute stock for display: use display_quantity from API, or sum variant inventories for unstitched
  const toggleSelectForSale = (productId) => {
    setSelectedForSale(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const toggleSelectAllForSale = () => {
    if (selectedForSale.size === products.length) setSelectedForSale(new Set());
    else setSelectedForSale(new Set(products.map(p => p.id)));
  };

  const handleMakeSale = (product) => {
    if (getProductStock(product) <= 0) {
      error('Product is out of stock');
      return;
    }
    navigate('/sales', { state: { prefillProducts: [product] } });
  };

  const handleMakeSaleSelected = () => {
    const toSell = products.filter(p => selectedForSale.has(p.id) && getProductStock(p) > 0);
    if (toSell.length === 0) {
      error('No products selected or all selected products are out of stock');
      return;
    }
    setSelectedForSale(new Set());
    navigate('/sales', { state: { prefillProducts: toSell } });
  };

  const getProductStock = (product) => {
    if (product.display_quantity !== undefined && product.display_quantity !== null && product.display_quantity !== '') {
      return parseFloat(product.display_quantity) || 0;
    }
    if (product.variants?.length > 0) {
      return product.variants.reduce((sum, v) => sum + getVariantQuantity(v), 0);
    }
    return parseFloat(product.quantity || 0) || 0;
  };

  const stitchedAverageCostPrice = () => {
    const valid = (stitchedSizeBreakdown || []).filter(r => (parseInt(r.quantity, 10) || 0) > 0);
    if (valid.length === 0) return 0;
    const costs = valid.map(r => parseFloat(r.costPrice) || 0);
    const sum = costs.reduce((a, b) => a + b, 0);
    return sum / costs.length;
  };

  const calculateTotalCost = () => {
    if (formData.productType === 'stitched') {
      return (stitchedSizeBreakdown || []).reduce((sum, r) => {
        const cost = parseFloat(r.costPrice) || 0;
        const qty = parseInt(r.quantity, 10) || 0;
        return sum + cost * qty;
      }, 0);
    } else {
      return unstitchedVariants.reduce((sum, v) => {
        const cost = parseFloat(v.costPrice) || 0;
        const guz = parseFloat(v.meters) || 0;
        return sum + cost * guz;
      }, 0);
    }
  };

  return (
    <Layout>
      <div className="products-page">
        <div className="page-header">
          <div>
            <h1>Products</h1>
            <p className="page-subtitle">Manage your product catalog and variants</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {selectedForSale.size > 0 && (
              <button onClick={handleMakeSaleSelected} className="sell-selected-btn">
                <HiCurrencyDollar />
                Sell Selected ({selectedForSale.size})
              </button>
            )}
            <Tooltip
              text={
                !hasBranches
                  ? 'Create a branch first'
                  : isAdmin && !effectiveBranchId
                    ? 'Select a branch in the header first'
                    : 'Download full stock report (PDF) for this branch — one row per variant, with vendor and value at cost'
              }
              position="bottom"
            >
              <button
                type="button"
                className="stock-report-pdf-btn"
                onClick={handleDownloadStockReportPdf}
                disabled={!hasBranches || stockReportPdfLoading || (isAdmin && !effectiveBranchId)}
              >
                <HiDocumentReport />
                <span>{stockReportPdfLoading ? 'Preparing…' : 'Stock report PDF'}</span>
              </button>
            </Tooltip>
            <Tooltip text={!hasBranches ? 'Create a branch first' : 'Add new product'} position="bottom">
              <button onClick={() => setShowModal(true)} className="create-button" disabled={!hasBranches}>
                <HiPlus />
                <span>Add Product</span>
              </button>
            </Tooltip>
          </div>
        </div>

        {!hasBranches && (
          <div className="banner no-branches-banner">
            Create at least one branch before adding parties, products, or accounts. Go to <a href="/branches">Branches</a> to add a branch.
          </div>
        )}
        {hasBranches && (currentBranch ? (
          <p className="products-branch-context">You're viewing and managing products for <strong>{currentBranch.name}</strong>. Change branch in the top bar to switch.</p>
        ) : isAdmin && (
          <p className="products-branch-context products-branch-context-warn">Select a branch in the top bar to add products or manage stock.</p>
        ))}
        <div className="filters-container">
          {user.role === 'super_admin' && (
            <div className="filter-dropdown business-selector">
              <select 
                value={selectedBusinessId} 
                onChange={(e) => setSelectedBusinessId(e.target.value)}
              >
                <option value="">Select Business</option>
                {businesses.map(business => (
                  <option key={business.id} value={business.id}>{business.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="search-bar">
            <HiSearch className="search-icon" />
            <input
              type="text"
              placeholder={showOurCreations ? 'Search by work order #, product, tailor, or vendor...' : 'Search by product name, product code, or vendor...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button className="search-btn" onClick={handleSearch}>Search</button>
            <Tooltip
              text={
                showOurCreations
                  ? 'Available on the product list view'
                  : !products.length
                    ? 'Search or load products first'
                    : searchTerm.trim()
                      ? `Download PDF for ${products.length} matched product(s)`
                      : `Download PDF for ${products.length} product(s) in the current list`
              }
              position="bottom"
            >
              <button
                type="button"
                className="product-list-pdf-btn"
                onClick={handleDownloadProductListPdf}
                disabled={showOurCreations || productListPdfLoading || loading || !products.length}
              >
                <HiDocumentReport />
                <span>{productListPdfLoading ? 'Preparing…' : 'List PDF'}</span>
              </button>
            </Tooltip>
          </div>
          <div className="filter-dropdown">
            <HiCollection className="filter-icon" />
            <select value={typeFilter} onChange={(e) => {
              setTypeFilter(e.target.value);
              setShowOurCreations(false); // Reset Our Creations when changing type filter
            }}>
              <option value="">All Types</option>
              <option value="stitched">Stitched</option>
              <option value="unstitched">Unstitched</option>
            </select>
          </div>
          <button
            className={`our-creations-btn ${showOurCreations ? 'active' : ''}`}
            onClick={() => {
              setShowOurCreations(!showOurCreations);
              if (!showOurCreations) {
                setTypeFilter(''); // Clear type filter when showing Our Creations
              }
            }}
            type="button"
          >
            <HiCollection className="filter-icon" />
            Our Creations
          </button>
        </div>

        {loading ? (
          <Loader message={showOurCreations ? 'Loading Our Creations...' : 'Loading products...'} variant="block" />
        ) : (
          <div className="table-container">
            {showOurCreations ? (
              filteredOurCreations.length === 0 ? (
                <div className="empty-state">
                  <p>No received work orders found. Receive work orders from tailors to see them here.</p>
                  <button onClick={() => navigate('/work-orders')} className="empty-state-button">
                    Go to Work Orders
                  </button>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Work Order #</th>
                      <th>Product</th>
                      <th>Product code</th>
                      <th>Fabric Vendor</th>
                      <th>Tailor</th>
                      <th>Business</th>
                      <th>Qty Received</th>
                      <th>Total Cost</th>
                      <th>Received Date</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOurCreations.map((wo) => (
                      <tr key={wo.id}>
                        <td className="font-mono">{wo.work_order_number}</td>
                        <td className="font-semibold">{wo.output_product?.name || '—'}</td>
                        <td className="font-mono">{wo.output_product?.product_code ?? wo.output_product?.productCode ?? '—'}</td>
                        <td>{wo.vendor?.name || wo.input_product?.vendor?.name || '—'}</td>
                        <td>{wo.tailor?.name || '—'}</td>
                        <td>{wo.business?.name || wo.branch?.business?.name || '—'}</td>
                        <td>
                          <span className="stock-badge in-stock">
                            {wo.received_output_quantity ?? wo.expected_output_quantity ?? 0}
                          </span>
                        </td>
                        <td>{formatPKR(wo.total_cost || 0)}</td>
                        <td className="created-at-cell">{wo.received_date ? formatDate(wo.received_date) : '—'}</td>
                        <td className="created-at-cell">{formatDate(wo.created_at)}</td>
                        <td>
                          <div className="action-buttons">
                            {wo.output_product?.id && (
                              <Tooltip text="View product" position="top">
                                <button className="action-btn view-btn" onClick={() => handleViewDetails(wo.output_product.id)}>
                                  <HiEye />
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : products.length === 0 ? (
              <div className="empty-state">
                <p>No products found.</p>
                <button onClick={() => setShowModal(true)} className="empty-state-button" disabled={!hasBranches}>
                  Add Your First Product
                </button>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="th-checkbox">
                      <input
                        type="checkbox"
                        checked={products.length > 0 && selectedForSale.size === products.length}
                        onChange={toggleSelectAllForSale}
                        title="Select all"
                      />
                    </th>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Vendor</th>
                    <th>Unit</th>
                    <th>Stock</th>
                    <th>Cost</th>
                    <th>Sale Price</th>
                    <th>Variants</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id}>
                      <td className="td-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedForSale.has(product.id)}
                          onChange={() => toggleSelectForSale(product.id)}
                          disabled={getProductStock(product) <= 0}
                          title={getProductStock(product) <= 0 ? 'Out of stock' : 'Select for sale'}
                        />
                      </td>
                      <td className="font-mono">{product.product_code}</td>
                      <td className="font-semibold">{product.name}</td>
                      <td>
                        <span className={`type-badge ${product.product_type}`}>
                          {product.product_type}
                        </span>
                      </td>
                      <td>{product.vendor?.name || '—'}</td>
                      <td>{product.unit_type}</td>
                      <td>
                        <span className={`stock-badge ${getProductStock(product) > 0 ? 'in-stock' : 'out-of-stock'}`}>
                          {product.unit_type === 'guz'
                            ? getProductStock(product).toFixed(2)
                            : getProductStock(product)}
                        </span>
                      </td>
                      <td>{(() => { const c = getLatestCostPrice(product) ?? product.cost_price; return c ? formatPKR(c) : '—'; })()}</td>
                      <td>{product.sale_price ? formatPKR(product.sale_price) : '—'}</td>
                      <td>{product.variants?.length || 0}</td>
                      <td className="created-at-cell">{formatDate(product.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          {getProductStock(product) > 0 && (
                            <Tooltip text="Make sale" position="top">
                              <button className="action-btn sell-btn" onClick={() => handleMakeSale(product)} title="Make sale">
                                <HiCurrencyDollar />
                              </button>
                            </Tooltip>
                          )}
                          <Tooltip text="View details" position="top">
                            <button className="action-btn view-btn" onClick={() => handleViewDetails(product.id)}>
                              <HiEye />
                            </button>
                          </Tooltip>
                          {canEditProduct && (
                            <>
                              <Tooltip text="Edit product" position="top">
                                <button className="action-btn edit-btn" onClick={() => handleEdit(product)}>
                                  <HiPencil />
                                </button>
                              </Tooltip>
                              <Tooltip text="Delete product" position="top">
                                <button className="action-btn delete-btn" onClick={() => handleDelete(product.id)}>
                                  <HiTrash />
                                </button>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Create/Edit Product Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={resetForm}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
              <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Product Type *</label>
                    <select
                      value={formData.productType}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setFormData({ 
                          ...formData, 
                          productType: newType,
                          unitType: newType === 'unstitched' ? 'guz' : 'piece',
                          // Auto-set vendor to In-House for stitched products
                          vendorId: newType === 'stitched' && !formData.vendorId ? (inHouseVendorId || '') : formData.vendorId,
                          // Clear cost fields when switching types
                          costPrice: '',
                          costPerMeter: '',
                          quantity: '',
                          totalMeters: ''
                        });
                        if (newType === 'unstitched') setUnstitchedVariants([{ color: '', meters: '', costPrice: '', salePrice: '' }]);
                        if (newType === 'stitched') setStitchedSizeBreakdown([{ size: '', quantity: '', costPrice: '', barcode: '' }]);
                      }}
                      required
                    >
                      <option value="stitched">Stitched</option>
                      <option value="unstitched">Unstitched</option>
                    </select>
                    <span className="field-hint">
                      {formData.productType === 'stitched' 
                        ? 'Unit type automatically set to "Piece"' 
                        : 'Unit type automatically set to "Guz"'}
                    </span>
                  </div>
                  <div className="form-group">
                    {/* Empty placeholder for alignment */}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Product Code *</label>
                    <input
                      type="text"
                      value={formData.productCode}
                      onChange={(e) => setFormData({ ...formData, productCode: e.target.value })}
                      placeholder="e.g. PRD-001"
                      required
                    />
                    <span className="field-hint">Required. Must be unique within your business. No spaces.</span>
                  </div>
                  <div className="form-group">
                    <label>Product Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g. Cotton Lawn 3pc"
                      required
                      minLength={2}
                    />
                    <span className="field-hint">Required. At least 2 characters.</span>
                  </div>
                </div>
                <div className="form-row vendor-assignment-row">
                  <div className="form-group vendor-search-wrapper" ref={vendorDropdownRef}>
                    <label>Vendor *</label>
                    <div className="vendor-search-row">
                      <div className={`vendor-search-input-wrapper${selectedVendor ? ' has-selection' : ''}`}>
                        <span className="vendor-search-icon-slot" aria-hidden="true">
                        <HiSearch className="vendor-search-icon" />
                        </span>
                        <input
                          type="text"
                          className="vendor-search-input"
                          placeholder="Type name, city, or phone to search..."
                          value={selectedVendor ? vendorDisplayText : vendorSearchTerm}
                          required={!formData.vendorId}
                          onChange={(e) => {
                            setVendorSearchTerm(e.target.value);
                            setShowVendorDropdown(true);
                            setFormData(prev => ({ ...prev, vendorId: '' }));
                          }}
                          onFocus={() => setShowVendorDropdown(true)}
                          readOnly={!!selectedVendor}
                          aria-label="Search and select vendor"
                        />
                        {selectedVendor && (
                          <button
                            type="button"
                            className="vendor-clear-btn"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, vendorId: '' }));
                              setVendorSearchTerm('');
                              setShowVendorDropdown(true);
                            }}
                            title="Clear selection"
                            aria-label="Clear vendor"
                          >
                            <HiX />
                          </button>
                        )}
                      </div>
                      <Tooltip text="Add new vendor" position="left">
                        <button
                          type="button"
                          onClick={() => setShowAddVendorModal(true)}
                          className="btn-add-vendor"
                          title="Add New Vendor"
                        >
                          <HiPlus /> Add
                        </button>
                      </Tooltip>
                    </div>
                    {showVendorDropdown && !selectedVendor && (
                      <div className="vendor-dropdown" role="listbox">
                        {filteredVendorsList.length === 0 ? (
                          <div className="vendor-dropdown-empty">
                            No vendors match. Try a different search or add a new vendor.
                          </div>
                        ) : (
                          <>
                            <div className="vendor-dropdown-header">
                              {vendorSearchTerm.trim() ? `${filteredVendorsList.length} vendor${filteredVendorsList.length !== 1 ? 's' : ''} found` : 'Select a vendor'}
                            </div>
                            {filteredVendorsList.map(vendor => (
                              <div
                                key={vendor.id}
                                className="vendor-dropdown-item"
                                role="option"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, vendorId: vendor.id.toString() }));
                                  setVendorSearchTerm('');
                                  setShowVendorDropdown(false);
                                }}
                              >
                                <span className="vendor-dropdown-name">{vendor.name}</span>
                                <span className="vendor-dropdown-meta">
                                  {[vendor.city, vendor.phone].filter(Boolean).join(' · ')}
                                  {(vendor.city || vendor.phone) && ' · '}
                                  <span className={partySearchBalanceClassName(vendor.balance, vendor.is_shop_expense)}>
                                    Balance: {formatPartySearchBalanceLabel(vendor.balance, vendor.is_shop_expense)}
                                  </span>
                                </span>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                    {formData.productType === 'stitched' && (
                      <span className="field-hint">
                        {formData.vendorId === inHouseVendorId?.toString() 
                          ? '✓ Set to In-House (stitched from unstitched clothing)' 
                          : 'For stitched products, select "In-House" if stitched from unstitched clothing'}
                      </span>
                    )}
                    <small className="form-hint vendor-search-hint">Only vendors are shown. Click a result to select.</small>
                  </div>
                </div>

                {!editingProduct && (
                      <div className="form-group">
                    <label>Purchase date (optional)</label>
                        <input
                      type="date"
                      value={formData.purchaseDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, purchaseDate: e.target.value }))}
                    />
                    <span className="field-hint">Used for the purchase record when a vendor is selected. Defaults to today if left empty.</span>
                      </div>
                )}

                {!editingProduct && (
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={!!postPurchaseToPartyLedger}
                        onChange={(e) => setPostPurchaseToPartyLedger(e.target.checked)}
                        disabled={!businessProductLedgerEnabled}
                      />
                      Post vendor bill to party ledger
                    </label>
                    <span className="field-hint">
                      {!businessProductLedgerEnabled
                        ? 'Disabled for this business: vendor party balance will not change from Add Product / Add Stock (bills may still be created).'
                        : 'When checked, vendor party ledger and balance reflect this purchase. Uncheck to record inventory/purchase only.'}
                    </span>
                  </div>
                )}

                {/* Conditional fields based on product type */}
                {formData.productType === 'stitched' ? (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Number of Units (Total) *</label>
                        <input
                          type="number"
                          min="0"
                          value={formData.quantity}
                          onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                          placeholder="e.g. 100"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Average cost price</label>
                        <div className="form-readonly-cost">
                          {formatPKR(stitchedAverageCostPrice())}
                        </div>
                        <span className="field-hint">Calculated from cost per size below (sum of variant costs ÷ number of variants).</span>
                      </div>
                    </div>
                    <div className="form-group stitched-size-breakdown-group">
                      <label>Size breakdown *</label>
                      <p className="size-breakdown-hint">Required. Add size, quantity, and cost price (Rs) per variant — sum of quantities must equal total units above.</p>
                      <div className="size-breakdown-list size-breakdown-horizontal">
                        {stitchedSizeBreakdown.map((row, idx) => {
                          const usedSizesElsewhere = stitchedSizeBreakdown
                            .filter((_, i) => i !== idx)
                            .map(r => (r.size != null && r.size !== '') ? r.size : '__one__');
                          const sizeOptions = [
                            { value: '', label: 'One Size' },
                            { value: 'S', label: 'S' },
                            { value: 'M', label: 'M' },
                            { value: 'L', label: 'L' },
                            { value: 'XL', label: 'XL' },
                            { value: 'XXL', label: 'XXL' },
                            { value: 'XXXL', label: 'XXXL' }
                          ].filter(opt => {
                            const key = opt.value === '' ? '__one__' : opt.value;
                            return !usedSizesElsewhere.includes(key) || key === (row.size === '' ? '__one__' : row.size);
                          });
                          return (
                          <div key={idx} className="size-breakdown-card">
                            <select
                              value={row.size ?? ''}
                              onChange={(e) => {
                                const next = [...stitchedSizeBreakdown];
                                next[idx] = { ...next[idx], size: e.target.value };
                                setStitchedSizeBreakdown(next);
                              }}
                              aria-label="Size"
                            >
                              {sizeOptions.map(opt => (
                                <option key={opt.value || '__one__'} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                            <input
                              type="number"
                              min="0"
                              placeholder="Qty"
                              value={row.quantity ?? ''}
                              onChange={(e) => {
                                const next = [...stitchedSizeBreakdown];
                                next[idx] = { ...next[idx], quantity: e.target.value };
                                setStitchedSizeBreakdown(next);
                              }}
                              aria-label="Quantity"
                            />
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="Cost (Rs)"
                              value={row.costPrice ?? ''}
                              onChange={(e) => {
                                const next = [...stitchedSizeBreakdown];
                                next[idx] = { ...next[idx], costPrice: e.target.value };
                                setStitchedSizeBreakdown(next);
                              }}
                              className="size-row-cost"
                              aria-label="Cost per variant"
                              title="Cost price for this size"
                            />
                            <input
                              type="text"
                              placeholder="Barcode (optional)"
                              value={row.barcode ?? ''}
                              onChange={(e) => {
                                const next = [...stitchedSizeBreakdown];
                                next[idx] = { ...next[idx], barcode: e.target.value };
                                setStitchedSizeBreakdown(next);
                              }}
                              className="size-row-barcode"
                              maxLength={100}
                              aria-label="Barcode"
                            />
                            <button
                              type="button"
                              className="btn-remove-size-row"
                              onClick={() => stitchedSizeBreakdown.length > 1 && setStitchedSizeBreakdown(stitchedSizeBreakdown.filter((_, i) => i !== idx))}
                              title={stitchedSizeBreakdown.length <= 1 ? 'At least one size required' : 'Remove size'}
                              disabled={stitchedSizeBreakdown.length <= 1}
                            >
                              <HiX />
                            </button>
                          </div>
                          );
                        })}
                        <button
                          type="button"
                          className="btn-add-size-row"
                          onClick={() => setStitchedSizeBreakdown([...stitchedSizeBreakdown, { size: '', quantity: '', costPrice: '', barcode: '' }])}
                        >
                          <HiPlus /> Add size
                        </button>
                      </div>
                      {(() => {
                        const totalFromSizes = stitchedSizeBreakdown.reduce((s, r) => s + (parseInt(r.quantity, 10) || 0), 0);
                        const totalUnits = parseInt(formData.quantity, 10) || 0;
                        const match = totalUnits > 0 && totalFromSizes === totalUnits;
                        return (
                          <div className={`size-breakdown-sum ${match ? 'match' : totalFromSizes > 0 ? 'mismatch' : ''}`}>
                            Size total: <strong>{totalFromSizes}</strong>
                            {totalUnits > 0 && (match ? ' ✓ Matches total units' : ` — total units: ${totalUnits}`)}
                          </div>
                        );
                      })()}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-group variants-input-group">
                      <label>Variants (Color + Guz + Cost & Sale per variant) *</label>
                      <span className="field-hint">Enter color, guz, cost (Rs/guz) and sale (Rs/guz) for each variant.</span>
                      <div className="variants-list unstitched-variants-with-prices">
                        {unstitchedVariants.map((v, idx) => (
                          <div key={idx} className="variant-row-card">
                            <div className="variant-row-fields">
                              <div className="variant-color-cell">
                                <ColorSelect
                                  value={v.color}
                                  onChange={(color) => {
                                    const next = [...unstitchedVariants];
                                    next[idx] = { ...next[idx], color };
                                    setUnstitchedVariants(next);
                                  }}
                                  placeholder="Color"
                                  excludeColors={unstitchedVariants.filter((vr, i) => i !== idx && vr.color).map(vr => vr.color)}
                                />
                              </div>
                              <div className="variant-meters-cell">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="Guz"
                                  value={v.meters}
                                  onChange={(e) => {
                                    const next = [...unstitchedVariants];
                                    next[idx] = { ...next[idx], meters: e.target.value };
                                    setUnstitchedVariants(next);
                                  }}
                                />
                              </div>
                              <div className="variant-cost-cell">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="Cost (Rs/guz)"
                                  value={v.costPrice ?? ''}
                                  onChange={(e) => {
                                    const next = [...unstitchedVariants];
                                    next[idx] = { ...next[idx], costPrice: e.target.value };
                                    setUnstitchedVariants(next);
                                  }}
                                  title="Cost per guz for this variant"
                                />
                              </div>
                              <div className="variant-sale-cell">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="Sale (Rs/guz)"
                                  value={v.salePrice ?? ''}
                                  onChange={(e) => {
                                    const next = [...unstitchedVariants];
                                    next[idx] = { ...next[idx], salePrice: e.target.value };
                                    setUnstitchedVariants(next);
                                  }}
                                  title="Sale per guz for this variant"
                                />
                              </div>
                              <button
                                type="button"
                                className="btn-remove-variant"
                                onClick={() => setUnstitchedVariants(unstitchedVariants.filter((_, i) => i !== idx))}
                                title="Remove variant"
                              >
                                <HiX />
                              </button>
                            </div>
                          </div>
                        ))}
                        <button type="button" className="btn-add-variant" onClick={() => setUnstitchedVariants([...unstitchedVariants, { color: '', meters: '', costPrice: '', salePrice: '' }])}>
                          <HiPlus /> Add Variant
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Product description..."
                    rows="3"
                  />
                </div>

                {/* Total Cost Bill */}
                <div className="cost-bill-section">
                  <div className="bill-header">
                    <h3>Total Cost Calculation</h3>
                  </div>
                  <div className="bill-content">
                    {formData.productType === 'stitched' ? (
                      <>
                        <div className="bill-row">
                          <span>Average cost price:</span>
                          <span>{formatPKR(stitchedAverageCostPrice())}</span>
                        </div>
                        <div className="bill-row">
                          <span>Variants:</span>
                          <span>
                            {stitchedSizeBreakdown.filter(r => (parseInt(r.quantity, 10) || 0) > 0).map((r, i) => (
                              <span key={i}>{r.size || 'One'}: {r.quantity} @ {formatPKR(parseFloat(r.costPrice) || 0)}</span>
                            )).reduce((acc, x, i) => i === 0 ? [x] : [...acc, ', ', x], [])}
                          </span>
                        </div>
                        <div className="bill-row">
                          <span>Number of Units:</span>
                          <span>{parseFloat(formData.quantity || 0).toFixed(0)} pieces</span>
                        </div>
                        <div className="bill-row bill-total">
                          <span><strong>Total Cost:</strong></span>
                          <span><strong>{formatPKR(calculateTotalCost())}</strong></span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="bill-row">
                          <span>Average cost price:</span>
                          <span>{formatPKR(unstitchedVariants.filter(v => v.color || v.meters).length ? (() => { const valid = unstitchedVariants.filter(v => v.color || v.meters); const costs = valid.map(v => parseFloat(v.costPrice) || 0); return costs.length ? costs.reduce((a, b) => a + b, 0) / costs.length : 0; })() : 0)}</span>
                        </div>
                        <div className="bill-row">
                          <span>Variants:</span>
                          <span>
                            {unstitchedVariants.filter(v => v.color || v.meters).map((v, i) => (
                              <span key={i}>{v.color}: {parseFloat(v.meters || 0).toFixed(2)} guz @ {formatPKR(parseFloat(v.costPrice) || 0)}</span>
                            )).reduce((acc, x, i) => i === 0 ? [x] : [...acc, ', ', x], [])}
                          </span>
                        </div>
                        <div className="bill-row">
                          <span>Total Guz:</span>
                          <span>{unstitchedVariants.reduce((s, v) => s + (parseFloat(v.meters) || 0), 0).toFixed(2)} guz</span>
                        </div>
                        <div className="bill-row bill-total">
                          <span><strong>Total Cost:</strong></span>
                          <span><strong>{formatPKR(calculateTotalCost())}</strong></span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={resetForm}>Cancel</button>
                  <button
                    type="submit"
                    disabled={submittingProduct || (!editingProduct && formData.productType === 'stitched' && (() => {
                      const totalUnits = parseInt(formData.quantity, 10) || 0;
                      const totalFromSizes = (stitchedSizeBreakdown || []).reduce((s, r) => s + (parseInt(r.quantity, 10) || 0), 0);
                      return totalUnits === 0 || totalFromSizes !== totalUnits;
                    })())}
                    title={!editingProduct && formData.productType === 'stitched' ? 'Size breakdown total must equal Number of Units' : ''}
                  >
                    {submittingProduct ? (editingProduct ? 'Updating...' : 'Creating...') : (editingProduct ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Vendor Modal */}
        {showAddVendorModal && (
          <div className="modal-overlay" onClick={() => setShowAddVendorModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Add New Vendor</h2>
                <button className="btn-close" onClick={() => setShowAddVendorModal(false)}>×</button>
              </div>
              <form onSubmit={handleCreateVendor}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Vendor Name *</label>
                    <input
                      type="text"
                      value={vendorFormData.name}
                      onChange={(e) => setVendorFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Ahmed Textiles"
                      required
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="text"
                        value={vendorFormData.phone}
                        onChange={(e) => setVendorFormData(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="e.g. +92 300 1234567"
                      />
                    </div>
                    <div className="form-group">
                      <label>City *</label>
                      <CitySelect
                        value={vendorFormData.city}
                        onChange={(city) => setVendorFormData(prev => ({ ...prev, city }))}
                        required
                        placeholder="Select city"
                        className="form-input"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <textarea
                      value={vendorFormData.address}
                      onChange={(e) => setVendorFormData(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Full address..."
                      rows="2"
                    />
                  </div>
                  <div className="form-group opening-balance-group">
                    <label>Opening Balance</label>
                    <div className="opening-balance-options">
                      <label className={`opening-balance-option ${vendorFormData.openingBalanceDirection === 'they_owe_you' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="vendorOpeningBalanceDirection"
                          checked={vendorFormData.openingBalanceDirection === 'they_owe_you'}
                          onChange={() => setVendorFormData(prev => ({ ...prev, openingBalanceDirection: 'they_owe_you' }))}
                        />
                        <span className="option-text">
                          <span className="option-label">Payment Received</span>
                        </span>
                      </label>
                      <label className={`opening-balance-option ${vendorFormData.openingBalanceDirection === 'you_owe_them' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="vendorOpeningBalanceDirection"
                          checked={vendorFormData.openingBalanceDirection === 'you_owe_them'}
                          onChange={() => setVendorFormData(prev => ({ ...prev, openingBalanceDirection: 'you_owe_them' }))}
                        />
                        <span className="option-text">
                          <span className="option-label">Payment Made</span>
                        </span>
                      </label>
                    </div>
                    <div className="opening-balance-amount-row">
                      <label className="amount-label">Amount (PKR)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={vendorFormData.openingBalanceAmount}
                        onChange={(e) => setVendorFormData(prev => ({ ...prev, openingBalanceAmount: e.target.value }))}
                        placeholder="0.00"
                        className="opening-balance-amount-input"
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={() => setShowAddVendorModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Add Vendor
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Product Details Modal */}
        {showDetailsModal && selectedProduct && (
          <div className="modal-overlay" onClick={() => { setShowDetailsModal(false); fetchProducts(); }}>
            <div className="modal-content modal-xlarge" onClick={(e) => e.stopPropagation()}>
              <div className="product-details-header">
                <div>
                  <h2>{selectedProduct.name}</h2>
                  <p className="product-code">{selectedProduct.product_code}</p>
                </div>
                <div className="product-details-header-actions">
                  <button
                    type="button"
                    className="btn-history-product"
                    onClick={handleOpenHistory}
                  >
                    History
                  </button>
                <span className={`type-badge ${selectedProduct.product_type}`}>
                  {selectedProduct.product_type}
                </span>
                </div>
              </div>
              
              <div className="product-info-grid">
                <div><strong>Unit Type:</strong> {selectedProduct.unit_type}</div>
                <div><strong>Stock Quantity:</strong>{' '}
                  <span className={`stock-badge ${(selectedProduct.variants?.length > 0
                    ? selectedProduct.variants.reduce((s, v) => s + getVariantQuantity(v), 0)
                    : selectedProduct.quantity || 0) > 0 ? 'in-stock' : 'out-of-stock'}`}>
                    {selectedProduct.variants?.length > 0
                      ? selectedProduct.variants.reduce((s, v) => s + getVariantQuantity(v), 0).toFixed(2)
                      : (selectedProduct.quantity || 0)}
                  </span>
                </div>
                <div><strong>{selectedProduct.variants?.length > 0 ? 'Average cost price' : 'Cost Price'}:</strong> {(() => { const c = getLatestCostPrice(selectedProduct); return c ? formatPKR(c) : '—'; })()}</div>
                <div><strong>Sale Price:</strong> {selectedProduct.variants?.length > 0 ? '(see variant table)' : (selectedProduct.sale_price ? formatPKR(selectedProduct.sale_price) : '—')}</div>
                <div><strong>Vendor:</strong> {selectedProduct.vendor?.name || 'Not specified'}</div>
                {selectedProduct.product_type === 'stitched' && selectedProduct.vendor?.name === 'In-House' && (
                  <div className="vendor-hint">✓ Stitched from unstitched clothing</div>
                )}
              </div>

              {selectedProduct.description && (
                <div className="product-description">
                  <strong>Description:</strong> {selectedProduct.description}
                </div>
              )}

              <div className="variants-section">
                <div className="variants-header">
                  <h3>Variants</h3>
                  <div className="variants-header-actions">
                    {(selectedProduct.product_type === 'unstitched' || (selectedProduct.product_type === 'stitched' && selectedProduct.vendor?.name !== 'In-House')) && selectedProduct.variants?.length > 0 && (
                      <button
                        type="button"
                        className="btn-add-stock-header"
                        onClick={() => {
                          const initial = {};
                          (selectedProduct.variants || []).forEach((v) => {
                            const cost = v.cost_price ?? selectedProduct?.cost_price;
                            initial[v.id] = { quantity: '', cost: cost != null && cost !== '' ? String(cost) : '' };
                          });
                          setAddStockRowInputs(initial);
                          setAddStockNewRows([]);
                          setAddStockPurchaseDate('');
                          setAddStockCreatedPurchase(null);
                          setAddStockUpdatePartyLedger(businessProductLedgerEnabled);
                          const defaultPartyId = selectedProduct?.vendor?.id ?? selectedProduct?.vendor_id ?? selectedProduct?.vendorId ?? '';
                          setAddStockBillPartyId(defaultPartyId ? String(defaultPartyId) : '');
                          setShowAddStockModal(true);
                        }}
                      >
                        <HiPlus /> Add Stock
                      </button>
                    )}
                    {canEditProduct && selectedProduct.product_type !== 'stitched' && (
                  <button className="add-variant-btn" onClick={() => setShowVariantModal(true)}>
                    <HiPlus /> Add Variant
                  </button>
                    )}
                  </div>
                </div>
                {selectedProduct.variants?.length > 0 ? (
                  (() => {
                    const allVariants = selectedProduct.variants;
                    return (
                  <table className="variants-table">
                    <thead>
                      <tr>
                        <th>Color</th>
                        <th>Size</th>
                        {selectedProduct.product_type === 'stitched' && <th>Barcode</th>}
                        <th>{selectedProduct.product_type === 'stitched' ? 'Stock (pieces)' : 'Stock (guz)'}</th>
                        <th>Cost Price</th>
                        <th>Sale Price</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allVariants.map((variant) => (
                        <tr key={variant.id}>
                          <td>
                            <span className={`color-chip ${isLightColor(variant.color) ? 'color-chip-light-bg' : ''}`} style={{ background: variant.color?.toLowerCase() || '#ccc' }}>
                              {variant.color}
                            </span>
                          </td>
                          <td>{variant.size || '—'}</td>
                          {selectedProduct.product_type === 'stitched' && <td>{variant.barcode || '—'}</td>}
                          <td>{getVariantQuantity(variant).toFixed(2)}</td>
                          <td>{variant.cost_price ? formatPKR(variant.cost_price) : (selectedProduct.cost_price ? formatPKR(selectedProduct.cost_price) : '—')}</td>
                          <td>{variant.sale_price ? formatPKR(variant.sale_price) : (selectedProduct.sale_price ? formatPKR(selectedProduct.sale_price) : '—')}</td>
                          <td className="variant-actions-cell">
                            {isAdmin && branchesList && branchesList.length >= 2 && isMainBranch && (selectedProduct?.product_type === 'stitched' || selectedProduct?.productType === 'stitched') && (
                              <Tooltip text="Transfer stock to another branch" position="top">
                              <button
                                type="button"
                                  className="btn-transfer-stock-small"
                                onClick={() => {
                                    setTransferVariant(variant);
                                    const cost = variant.cost_price ?? selectedProduct?.cost_price;
                                    const mainBranch = (branchesList || []).find((b) => b.is_main || b.isMain);
                                    const mainBranchId = mainBranch ? String(mainBranch.id) : (branchesList?.[0] ? String(branchesList[0].id) : '');
                                    setTransferForm({
                                      fromBranchId: mainBranchId,
                                      toBranchId: '',
                                    quantity: '',
                                      unitPrice: cost != null && cost !== '' ? String(cost) : '',
                                      transferDate: getTodayStr()
                                  });
                                    setShowTransferModal(true);
                                }}
                              >
                                  <HiSwitchHorizontal className="btn-transfer-icon" aria-hidden />
                                  <span>Transfer</span>
                              </button>
                              </Tooltip>
                            )}
                            {canEditProduct && (
                            <button
                              type="button"
                              className="btn-update-price-small"
                              onClick={() => handleUpdatePrice(variant)}
                            >
                              Update price
                            </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                    );
                  })()
                ) : (
                  <p className="no-variants">No variants added yet.</p>
                )}
              </div>

              <div className="modal-actions">
                <button onClick={() => { setShowDetailsModal(false); fetchProducts(); }}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Product History Modal */}
        {showHistoryModal && selectedProduct && (
          <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
            <div className="modal-content modal-xlarge" onClick={(e) => e.stopPropagation()}>
              <h2>Product history – {selectedProduct.name}</h2>
              <p className="product-history-subtitle">Sales, transfers, stock updates, and returns for the current branch. Default: last 6 months.</p>
              <div className="product-history-filters">
                <div className="form-group product-history-branch-display">
                  <label>Branch</label>
                  <span className="product-history-branch-value">
                    {(branchesList || []).find(b => String(b.id) === String(historyFilters.branchId))?.name || currentBranch?.name || '—'}
                  </span>
                </div>
                <div className="form-group">
                  <label>From date</label>
                  <input
                    type="date"
                    value={historyFilters.fromDate}
                    onChange={(e) => setHistoryFilters(prev => ({ ...prev, fromDate: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>To date</label>
                  <input
                    type="date"
                    value={historyFilters.toDate}
                    onChange={(e) => setHistoryFilters(prev => ({ ...prev, toDate: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={historyFilters.type}
                    onChange={(e) => setHistoryFilters(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <option value="">All</option>
                    <option value="sale">Sale</option>
                    <option value="sale_return">Sale return</option>
                    <option value="transfer_in">Transfer in</option>
                    <option value="transfer_out">Transfer out</option>
                    <option value="adjustment">Stock update</option>
                    <option value="purchase">Purchase</option>
                    <option value="purchase_return">Purchase return</option>
                  </select>
                </div>
                <div className="form-group product-history-apply">
                  <button type="button" className="btn-primary" onClick={handleApplyHistory} disabled={historyLoading}>
                    {historyLoading ? 'Loading...' : 'Apply'}
                  </button>
                </div>
              </div>
              <div className="product-history-table-wrap">
                {historyLoading ? (
                  <Loader message="Loading history..." variant="block" />
                ) : historyGrouped.length === 0 ? (
                  <p className="product-history-empty">
                    No movements found for the selected period. Change dates or type and click Apply.
                  </p>
                ) : (
                  <table className="product-history-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Total quantity</th>
                        <th>Customer / Branch</th>
                        <th>Reference</th>
                        <th className="product-history-view-col"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyGrouped.map((group) => (
                        <React.Fragment key={group.key}>
                          <tr>
                            <td>{formatDate(group.date)}</td>
                            <td><span className={`history-type-badge ${group.movementType}`}>{group.typeLabel || group.movementType}</span></td>
                            <td className={group.totalQuantity >= 0 ? 'qty-in' : 'qty-out'}>
                              {group.totalQuantity >= 0 ? '+' : ''}{group.totalQuantity}
                            </td>
                            <td>{group.customerOrBranch || '—'}</td>
                            <td>
                              <div>{group.referenceNumber || '—'}</div>
                              {group.partyLedgerNotice && (
                                <div className="product-history-ledger-notice" role="note">
                                  {group.partyLedgerNotice}
                                </div>
                              )}
                            </td>
                            <td className="product-history-view-col">
                              <button
                                type="button"
                                className={`btn-icon btn-view-variant-history ${expandedGroupKey === group.key ? 'expanded' : ''}`}
                                onClick={() => handleToggleVariantBreakdown(group.key)}
                                title={expandedGroupKey === group.key ? 'Hide variant breakdown' : 'View variant breakdown'}
                                aria-label={expandedGroupKey === group.key ? 'Hide variant breakdown' : 'View variant breakdown'}
                              >
                                <HiEye />
                              </button>
                            </td>
                          </tr>
                          {expandedGroupKey === group.key && group.variants && group.variants.length > 0 && (
                            <tr key={`${group.key}-detail`} className="product-history-detail-row">
                              <td colSpan={6}>
                                <div className="product-history-variant-breakdown">
                                  <table className="product-history-variant-table">
                                    <thead>
                                      <tr>
                                        <th>Variant</th>
                                        <th>Quantity</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {group.variants.map((v) => (
                                        <tr key={v.id}>
                                          <td>{v.variantLabel}</td>
                                          <td className={v.quantityChange >= 0 ? 'qty-in' : 'qty-out'}>
                                            {v.quantityChange >= 0 ? '+' : ''}{v.quantityChange}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowHistoryModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Add Variant Modal */}
        {showVariantModal && (
          <div className="modal-overlay" onClick={() => setShowVariantModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Add Variant</h2>
              <form onSubmit={handleAddVariant}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Color *</label>
                    <ColorSelect
                      value={variantFormData.color}
                      onChange={(color) => setVariantFormData({ ...variantFormData, color })}
                      placeholder="Select color"
                      excludeColors={(selectedProduct?.variants || []).map(v => v.color).filter(Boolean)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Size</label>
                    <select
                      value={variantFormData.size}
                      onChange={(e) => setVariantFormData({ ...variantFormData, size: e.target.value })}
                    >
                      <option value="">No Size</option>
                      <option value="S">S</option>
                      <option value="M">M</option>
                      <option value="L">L</option>
                      <option value="XL">XL</option>
                      <option value="XXL">XXL</option>
                      <option value="XXXL">XXXL</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Cost Price (Override)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={variantFormData.costPrice}
                      onChange={(e) => setVariantFormData({ ...variantFormData, costPrice: e.target.value })}
                      placeholder="Leave empty for default"
                    />
                  </div>
                  <div className="form-group">
                    <label>Sale Price (Override)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={variantFormData.salePrice}
                      onChange={(e) => setVariantFormData({ ...variantFormData, salePrice: e.target.value })}
                      placeholder="Leave empty for default"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>SKU</label>
                    <input
                      type="text"
                      value={variantFormData.sku}
                      onChange={(e) => setVariantFormData({ ...variantFormData, sku: e.target.value })}
                      placeholder="e.g. PRD-001-BLU-M"
                    />
                  </div>
                  {selectedProduct?.product_type === 'stitched' && (
                  <div className="form-group">
                    <label>Barcode</label>
                    <input
                      type="text"
                      value={variantFormData.barcode}
                      onChange={(e) => setVariantFormData({ ...variantFormData, barcode: e.target.value })}
                      placeholder="Scan or enter barcode"
                    />
                  </div>
                )}
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => setShowVariantModal(false)}>Cancel</button>
                  <button type="submit">Add Variant</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Stock Modal - table of variants + purchase detail for bill */}
        {showAddStockModal && selectedProduct && (
          <div className="modal-overlay" onClick={() => { if (!addStockSubmitting) { setShowAddStockModal(false); setAddStockRowInputs({}); setAddStockNewRows([]); setAddStockPurchaseDate(''); } }}>
            <div className="modal-content add-stock-modal-wide" onClick={(e) => e.stopPropagation()}>
              <h2>Add Stock</h2>
              <p className="add-stock-variant-info">
                Product: <strong>{selectedProduct?.name}</strong>
                {selectedProduct?.vendor?.name && (
                  <> · Vendor: <strong>{selectedProduct.vendor.name}</strong></>
                )}
              </p>
              {currentBranch ? (
                <p className="add-stock-branch-notice">You're adding stock to <strong>{currentBranch.name}</strong>. Change branch in the top bar if needed.</p>
              ) : (
                <p className="add-stock-branch-notice add-stock-branch-warn">Select a branch in the top bar to add stock.</p>
              )}
              <form onSubmit={handleAddStock}>
                <div className="add-stock-table-wrap">
                  <table className="add-stock-table">
                    <thead>
                      <tr>
                        <th>Variant</th>
                        <th>Current stock</th>
                        <th>Cost price</th>
                        <th>{selectedProduct?.unit_type === 'piece' ? 'Qty to add (pcs)' : 'Qty to add (guz)'}</th>
                        <th>{selectedProduct?.unit_type === 'piece' ? 'Cost/piece (Rs)' : 'Cost/guz (Rs)'}</th>
                        <th className="add-stock-th-action"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedProduct.variants || []).map((v) => {
                        const row = addStockRowInputs[v.id] ?? { quantity: '', cost: '' };
                        const currentQty = getVariantQuantity(v);
                        const costDisplay = v.cost_price ?? selectedProduct?.cost_price;
                        return (
                          <tr key={v.id}>
                            <td>
                              {(selectedProduct?.product_type === 'stitched' || selectedProduct?.productType === 'stitched') ? (
                                <span className="add-stock-variant-size"><strong>{v.size || '—'}</strong></span>
                              ) : (
                                <>
                                  <span className={`color-chip ${isLightColor(v.color) ? 'color-chip-light-bg' : ''}`} style={{ background: (v.color || '').toLowerCase() || '#ccc' }}>
                                    {v.color}
                                  </span>
                                  {v.size ? <span className="add-stock-variant-size"> · {v.size}</span> : null}
                                </>
                              )}
                            </td>
                            <td>{currentQty.toFixed(2)}</td>
                            <td>{costDisplay != null ? formatPKR(costDisplay) : '—'}</td>
                            <td>
                              <input
                                type="number"
                                step={selectedProduct?.unit_type === 'piece' ? '1' : '0.01'}
                                min="0"
                                value={row.quantity}
                                onChange={(e) => setAddStockRowInputs((prev) => ({ ...prev, [v.id]: { ...prev[v.id], quantity: e.target.value } }))}
                                placeholder="0"
                                className="add-stock-input-qty"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={row.cost}
                                onChange={(e) => setAddStockRowInputs((prev) => ({ ...prev, [v.id]: { ...prev[v.id], cost: e.target.value } }))}
                                placeholder="Optional"
                                className="add-stock-input-cost"
                              />
                            </td>
                            <td></td>
                          </tr>
                        );
                      })}
                      {addStockNewRows.map((r) => (
                        <tr key={r.id} className="add-stock-row-new">
                          <td>
                            <div className="add-stock-new-variant-fields">
                              {(selectedProduct?.product_type === 'stitched' || selectedProduct?.productType === 'stitched') ? (
                                <select
                                  value={r.size}
                                  onChange={(e) => setAddStockNewRows((prev) => prev.map((x) => x.id === r.id ? { ...x, size: e.target.value } : x))}
                                  className="add-stock-input-size"
                                  disabled={addStockSubmitting}
                                >
                                  <option value="">Select size *</option>
                                  {(() => {
                                    const COMMON_SIZES = ['One Size', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
                                    const existingSizes = (selectedProduct?.variants || []).map(v => (v.size || '').trim()).filter(Boolean);
                                    const selectedOtherNew = (addStockNewRows || [])
                                      .filter(x => x.id !== r.id)
                                      .map(x => (x.size || '').trim())
                                      .filter(Boolean);
                                    const blocked = new Set(
                                      [...existingSizes, ...selectedOtherNew].map(s => s.toLowerCase())
                                    );
                                    return COMMON_SIZES
                                      .filter(s => !blocked.has(s.toLowerCase()))
                                      .map(s => <option key={s} value={s}>{s}</option>);
                                  })()}
                                </select>
                              ) : (
                                <ColorSelect
                                  value={r.color}
                                  onChange={(val) => setAddStockNewRows((prev) => prev.map((x) => x.id === r.id ? { ...x, color: val } : x))}
                                  placeholder="Select color *"
                                  disabled={addStockSubmitting}
                                  excludeColors={[
                                    ...(selectedProduct?.variants || []).map(v => (v.color || '').trim()).filter(Boolean),
                                    ...(addStockNewRows || []).filter(x => x.id !== r.id).map(x => (x.color || '').trim()).filter(Boolean)
                                  ]}
                                />
                              )}
                            </div>
                          </td>
                          <td>0</td>
                          <td>—</td>
                          <td>
                            <input
                              type="number"
                              step={selectedProduct?.unit_type === 'piece' ? '1' : '0.01'}
                              min="0"
                              value={r.quantity}
                              onChange={(e) => setAddStockNewRows((prev) => prev.map((x) => x.id === r.id ? { ...x, quantity: e.target.value } : x))}
                              placeholder="0"
                              className="add-stock-input-qty"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={r.cost}
                              onChange={(e) => setAddStockNewRows((prev) => prev.map((x) => x.id === r.id ? { ...x, cost: e.target.value } : x))}
                              placeholder="Optional"
                              className="add-stock-input-cost"
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="add-stock-remove-new"
                              onClick={() => setAddStockNewRows((prev) => prev.filter((x) => x.id !== r.id))}
                              aria-label="Remove new variant"
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="add-stock-add-new-wrap">
                    <button
                      type="button"
                      className="add-stock-add-new-btn"
                      onClick={() => setAddStockNewRows((prev) => [...prev, { id: `new-${Date.now()}`, color: '', size: '', quantity: '', cost: '' }])}
                      disabled={addStockSubmitting}
                    >
                      <HiPlus /> Add new {(selectedProduct?.product_type === 'stitched' || selectedProduct?.productType === 'stitched') ? 'size' : 'color'} variant
                    </button>
                  </div>
                </div>
                <div className="add-stock-purchase-detail">
                  <h3 className="add-stock-purchase-heading">Purchase detail (for bill / party ledger)</h3>
                  {(() => {
                    const isStitched = selectedProduct?.product_type === 'stitched' || selectedProduct?.productType === 'stitched';
                    const total = (() => {
                      let sum = 0;
                      (selectedProduct?.variants || []).forEach((v) => {
                        const row = addStockRowInputs[v.id] || { quantity: '', cost: '' };
                        const q = parseFloat(row.quantity);
                        if (isNaN(q) || q <= 0) return;
                        const enteredCost = row.cost !== '' && row.cost != null ? parseFloat(row.cost) : null;
                        const c = (enteredCost != null && !isNaN(enteredCost))
                          ? enteredCost
                          : (v.cost_price != null ? parseFloat(v.cost_price) : (selectedProduct?.cost_price != null ? parseFloat(selectedProduct.cost_price) : 0));
                        sum += q * (parseFloat(c) || 0);
                      });
                      (addStockNewRows || []).forEach((r) => {
                        const q = parseFloat(r.quantity);
                        if (isNaN(q) || q <= 0) return;
                        const enteredCost = r.cost !== '' && r.cost != null ? parseFloat(r.cost) : null;
                        const c = (enteredCost != null && !isNaN(enteredCost)) ? enteredCost : (selectedProduct?.cost_price != null ? parseFloat(selectedProduct.cost_price) : 0);
                        sum += q * (parseFloat(c) || 0);
                      });
                      return Math.round(sum * 100) / 100;
                    })();

                    const partiesForBill = (vendors || []).map(v => ({ id: v.id, name: v.name }));
                    const selectedPartyName =
                      (selectedProduct?.vendor?.id && String(selectedProduct.vendor.id) === String(addStockBillPartyId) ? selectedProduct.vendor.name : null)
                      || (partiesForBill.find(p => String(p.id) === String(addStockBillPartyId))?.name)
                      || selectedProduct?.vendor?.name
                      || '—';

                    return (
                      <div style={{ marginTop: '0.75rem', borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem' }}>
                        <div style={{ border: '2px solid #7c3aed', background: '#f5f3ff', borderRadius: '10px', padding: '10px 12px', marginBottom: '0.75rem' }}>
                          <div style={{ fontWeight: 800, color: '#5b21b6', marginBottom: '0.35rem' }}>
                            Bill party (most important)
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>
                              Bill goes to party
                              <span style={{ marginLeft: '0.5rem', fontSize: '0.78rem', color: '#6b7280' }}>
                                (default: product vendor)
                              </span>
                            </label>
                            <select
                              value={addStockBillPartyId || ''}
                              onChange={(e) => setAddStockBillPartyId(e.target.value)}
                              disabled={addStockSubmitting}
                              style={{ fontWeight: 700 }}
                            >
                              <option value="">Use product vendor ({selectedProduct?.vendor?.name || 'Not set'})</option>
                              {(vendors || []).map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                              ))}
                            </select>
                            <span className="form-hint">This selected party will be attached to this bill.</span>
                          </div>
                        </div>

                        <div className="form-group">
                          <label>Purchase date (optional)</label>
                          <input
                            type="date"
                            value={addStockPurchaseDate}
                            onChange={(e) => setAddStockPurchaseDate(e.target.value)}
                          />
                          <span className="form-hint">Used for the purchase/bill record. Defaults to today if left empty.</span>
                        </div>

                <div className="form-row">
                          <div className="form-group" style={{ minWidth: 220 }}>
                            <label>Bill total</label>
                            <div style={{ fontWeight: 700, fontSize: '1.05rem', paddingTop: '0.4rem' }}>
                              {formatPKR(total)}
                            </div>
                            <span className="form-hint">Calculated from Qty × Cost.</span>
                          </div>
                        </div>

                        <div className="form-group" style={{ marginTop: '-0.25rem' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={!!addStockUpdatePartyLedger}
                              onChange={(e) => setAddStockUpdatePartyLedger(e.target.checked)}
                              disabled={addStockSubmitting || !businessProductLedgerEnabled}
                            />
                            Update party ledger for this bill
                          </label>
                          <span className="form-hint">
                            {!businessProductLedgerEnabled
                              ? 'Disabled for this business: the bill can still be created without changing party balance.'
                              : addStockUpdatePartyLedger
                                ? `This will update "${selectedPartyName}" party ledger by ${formatPKR(total)}.`
                                : 'A bill will be created, but party ledger/balance will not be updated.'}
                          </span>
                        </div>

                        {addStockCreatedPurchase && (
                          <div style={{ marginTop: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px' }}>
                            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Bill created</div>
                            <div><strong>Bill #:</strong> {addStockCreatedPurchase.purchase_number || addStockCreatedPurchase.purchaseNumber || addStockCreatedPurchase.id}</div>
                            {(addStockCreatedPurchase.purchase_date || addStockCreatedPurchase.purchaseDate) && (
                              <div><strong>Date:</strong> {new Date(addStockCreatedPurchase.purchase_date || addStockCreatedPurchase.purchaseDate).toLocaleDateString()}</div>
                            )}
                            {(addStockCreatedPurchase.total_amount != null || addStockCreatedPurchase.totalAmount != null) && (
                              <div><strong>Total:</strong> {formatPKR(addStockCreatedPurchase.total_amount ?? addStockCreatedPurchase.totalAmount)}</div>
                            )}
                            <div style={{ marginTop: '0.5rem' }}>
                              <button type="button" className="btn-secondary btn-sm" onClick={() => navigate('/purchases')}>
                                View in Purchases
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => { if (!addStockSubmitting) { setShowAddStockModal(false); setAddStockRowInputs({}); setAddStockNewRows([]); setAddStockPurchaseDate(''); setAddStockCreatedPurchase(null); } }} disabled={addStockSubmitting}>Close</button>
                  <button type="submit" disabled={(isAdmin && !effectiveBranchId) || addStockSubmitting}>
                    {addStockSubmitting ? 'Adding...' : 'Add Stock'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Transfer Stock Modal - same product/variant, editable unit price for branch ledger */}
        {showTransferModal && transferVariant && (
          <div className="modal-overlay" onClick={() => { if (!transferSubmitting) { setShowTransferModal(false); setTransferVariant(null); setTransferForm({ fromBranchId: '', toBranchId: '', quantity: '', unitPrice: '', transferDate: '' }); } }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Transfer stock to branch</h2>
              <p className="transfer-product-info">
                Product: <strong>{selectedProduct?.name}</strong> · Code: <strong>{selectedProduct?.product_code}</strong>
                <br />
                Variant: <strong>{transferVariant.color}</strong>{transferVariant.size ? ` · ${transferVariant.size}` : ''}
              </p>
              <form onSubmit={handleTransferStock}>
                {(() => {
                  const mainBranch = (branchesList || []).find((b) => b.is_main || b.isMain);
                  const fromBranchIdFixed = mainBranch ? String(mainBranch.id) : (transferForm.fromBranchId || (branchesList?.[0] ? String(branchesList[0].id) : ''));
                  return (
                    <>
                  <div className="form-group">
                  <label>From branch</label>
                  <div className="transfer-from-branch-readonly">
                    {mainBranch ? `${mainBranch.name} (Main)` : (branchesList?.find((b) => String(b.id) === fromBranchIdFixed)?.name ?? 'Main branch')}
                  </div>
                </div>
                <div className="form-group">
                  <label>To branch *</label>
                  <select
                    value={transferForm.toBranchId}
                    onChange={(e) => setTransferForm({ ...transferForm, toBranchId: e.target.value })}
                    required
                  >
                    <option value="">Select branch</option>
                    {branchesList.filter((b) => String(b.id) !== fromBranchIdFixed).map((b) => (
                      <option key={b.id} value={b.id}>{b.name} {b.is_main || b.isMain ? '(Main)' : ''}</option>
                    ))}
                  </select>
                </div>
                    </>
                  );
                })()}
                <div className="form-group">
                  <label>Quantity *</label>
                  {(() => {
                    const mainBranchForQty = (branchesList || []).find((b) => b.is_main || b.isMain);
                    const fromIdForQty = mainBranchForQty ? String(mainBranchForQty.id) : transferForm.fromBranchId;
                    const maxAvail = getVariantQuantityInBranch(transferVariant, fromIdForQty);
                    const qtyNum = parseFloat(transferForm.quantity);
                    const exceedsMax = maxAvail != null && !isNaN(qtyNum) && qtyNum > maxAvail;
                    return (
                      <>
                    <input
                      type="number"
                          step={selectedProduct?.unit_type === 'piece' ? '1' : '0.01'}
                          min={selectedProduct?.unit_type === 'piece' ? '1' : '0.01'}
                          value={transferForm.quantity}
                          onChange={(e) => setTransferForm({ ...transferForm, quantity: e.target.value })}
                          placeholder={selectedProduct?.unit_type === 'piece' ? 'e.g. 10' : 'e.g. 50'}
                      required
                          className={exceedsMax ? 'input-error' : ''}
                        />
                        {maxAvail != null && (
                          <p className="helper-text transfer-qty-helper">
                            Available: {maxAvail}
                            {selectedProduct?.unit_type === 'piece' ? ' pcs' : ''}
                          </p>
                        )}
                        {exceedsMax && (
                          <p className="transfer-qty-max-error">Max limit is {maxAvail}. You cannot transfer more than available stock.</p>
                        )}
                      </>
                    );
                  })()}
                  </div>
                  <div className="form-group">
                  <label>Unit price (for branch ledger)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                    value={transferForm.unitPrice}
                    onChange={(e) => setTransferForm({ ...transferForm, unitPrice: e.target.value })}
                    placeholder="Optional – used for branch ledger value"
                  />
                  <p className="helper-text">Optional. Same product/variant; you can edit the transfer price for ledger.</p>
                  </div>
                <div className="form-group">
                  <label>Transfer date (optional)</label>
                  <input
                    type="date"
                    value={transferForm.transferDate}
                    onChange={(e) => setTransferForm({ ...transferForm, transferDate: e.target.value })}
                  />
                  <p className="helper-text">Used for branch/party ledger. Defaults to today if left empty.</p>
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => { if (!transferSubmitting) { setShowTransferModal(false); setTransferVariant(null); setTransferForm({ fromBranchId: '', toBranchId: '', quantity: '', unitPrice: '', transferDate: '' }); } }}>Cancel</button>
                  <button type="submit" disabled={transferSubmitting}>{transferSubmitting ? 'Transferring...' : 'Transfer'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Update Price Modal - for existing variants (stitched & unstitched) */}
        {editingVariantForPrice && (
          <div className="modal-overlay" onClick={() => { setEditingVariantForPrice(null); setUpdatePriceFormData({ costPrice: '', salePrice: '' }); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Update price</h2>
              <p className="update-price-variant-info">
                {selectedProduct?.name} · {editingVariantForPrice.color}{editingVariantForPrice.size ? ` · ${editingVariantForPrice.size}` : ''}
              </p>
              <form onSubmit={handleUpdatePriceSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Cost price</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={updatePriceFormData.costPrice}
                      onChange={(e) => setUpdatePriceFormData({ ...updatePriceFormData, costPrice: e.target.value })}
                      placeholder="Override product default"
                    />
                  </div>
                  <div className="form-group">
                    <label>Sale price</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={updatePriceFormData.salePrice}
                      onChange={(e) => setUpdatePriceFormData({ ...updatePriceFormData, salePrice: e.target.value })}
                      placeholder="Override product default"
                    />
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => { setEditingVariantForPrice(null); setUpdatePriceFormData({ costPrice: '', salePrice: '' }); }}>Cancel</button>
                  <button type="submit">Save prices</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => { setShowConfirmModal(false); setProductToDelete(null); }}
          onConfirm={confirmDelete}
          title="Delete Product"
          message="Are you sure you want to delete this product and all its variants? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />
      </div>
    </Layout>
  );
};

export default Products;
